import React, { memo, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AnyAction } from 'redux';
import { Set, Map as ImmutableMap } from 'immutable';
import { getDocumentsForBulkSendToPay } from '@/domain/documents';
import { bulkCreateDocumentPaymentAction } from '@/domain/documents/documentsActions';
import type { BulkCreateDocumentPaymentsResponse } from '@/domain/documents/schemas';
import * as ACL from '@/domain/restriction';
import { promisify } from '@/lib/helpers';
import { useIntl } from 'react-intl';
import { useConfirm } from 'material-ui-confirm';

import Tooltip from '@/components/mui/Tooltip/TooltipForButton';
import toast from '@/components/Toast';
import IconButton from '@mui/material/IconButton';
import PaymentIcon from '@mui/icons-material/Payment';

// bulkCreateDocumentPaymentAction omits the `asyncAction` payload generic, so it defaults to `void`.
type BulkCreatePaymentsResolvers = {
  resolve: (result: BulkCreateDocumentPaymentsResponse) => void;
  reject: (err: unknown) => void;
};

const bulkCreatePayments = bulkCreateDocumentPaymentAction as unknown as (
  payload: { documentsIds: string[] } & BulkCreatePaymentsResolvers,
) => AnyAction;

interface BulkSendToPayButtonProps {
  selectedIds: Set<string>;
  clearSelection: () => void;
}

const BulkSendToPayButton: React.FC<BulkSendToPayButtonProps> = ({ selectedIds, clearSelection }) => {
  const { formatMessage } = useIntl();
  const isGranted = useSelector(ACL.isGranted);
  const documentsForBulkSendToPay = useSelector(getDocumentsForBulkSendToPay(selectedIds)) as ImmutableMap<
    string,
    unknown
  >;
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const availableCount = documentsForBulkSendToPay.size;
  const isHidden = !isGranted(ACL.CAN_SEND_TO_DOKKA_PAY) || availableCount === 0;
  const documentIDs: string[] = useMemo(
    () => documentsForBulkSendToPay.keySeq().toArray(),
    [documentsForBulkSendToPay],
  );

  const handleSendToPay = useCallback(async () => {
    const fn = (arg: BulkCreatePaymentsResolvers) =>
      dispatch(bulkCreatePayments({ documentsIds: documentIDs, ...arg }));
    // On failure the saga owns the error toast.
    const result = (await promisify(fn).catch(() => null)) as BulkCreateDocumentPaymentsResponse | null;

    if (!result) return;

    toast.success(
      formatMessage(
        {
          id: 'documents.bulkActions.sendToPay',
          defaultMessage: '{success} out of {total} documents were sent to Pay',
        },
        {
          success: result.success,
          total: selectedIds.size,
        },
      ),
    );
    clearSelection();
  }, [documentIDs, clearSelection, selectedIds, dispatch, formatMessage]);

  const handleClick = useCallback(() => {
    confirm({
      title: formatMessage({
        id: 'confirm.selectPanel.bulkSendToPay.title',
        defaultMessage: 'Send to Pay?',
      }),
      description: formatMessage(
        {
          id: 'confirm.selectPanel.bulkSendToPay.placeholder',
          defaultMessage: '{available} of {total} documents will be sent to Pay.',
        },
        {
          total: selectedIds.size,
          available: availableCount,
        },
      ),
      confirmationText: formatMessage({
        id: 'confirm.selectPanel.bulkSendToPay.btn',
        defaultMessage: 'Send',
      }),
    }).then(() => {
      handleSendToPay();
    });
  }, [handleSendToPay, confirm, formatMessage, availableCount, selectedIds.size]);

  if (isHidden) return null;

  return (
    <Tooltip t={{ id: 'selectedPanel.sendToPay', defaultMessage: 'Send to Pay' }} placement="top">
      <IconButton aria-label="sendToPay" color="primary" onClick={handleClick}>
        <PaymentIcon />
      </IconButton>
    </Tooltip>
  );
};

export default memo(BulkSendToPayButton);
